import React from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import logger from '../../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    logger.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <>
          <Helmet>
            <title>Error | SmartShield</title>
          </Helmet>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '50vh',
              p: 3,
            }}
          >
            <Card sx={{ maxWidth: 600, width: '100%' }}>
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h4" color="error" gutterBottom>
                  Oops! Something went wrong
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  We encountered an unexpected error. Please try refreshing the page or contact support if the problem
                  persists.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button variant="contained" color="primary" onClick={this.handleRetry}>
                    Try Again
                  </Button>
                  <Button variant="outlined" onClick={() => window.location.reload()}>
                    Refresh Page
                  </Button>
                </Box>
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="caption" component="pre" sx={{ textAlign: 'left' }}>
                      {this.state.error.toString()}
                      {this.state.errorInfo.componentStack}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
